
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, ChevronRight, Dices, Focus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PromptDisplay from "../PromptDisplay";
import { useSettings } from "@/context/SettingsContext";
import FeedbackEmojis from "../FeedbackEmojis";
import LensFocusDialog from "../LensFocusDialog";
import { PromptStage } from "@/hooks/dice/useRollerState";

interface ResultSectionProps {
  diceValue: number;
  promptStage: PromptStage;
  rollAgain: () => void;
  advancePrompt?: () => void;
  reRollPrompt?: () => void;
  twistActive?: boolean;
  activateTwist?: () => void;
  isSpinning?: boolean;
  rerollCount?: number;
  onFeedback?: (feedback: "positive" | "neutral" | "negative") => void;
  onSelectLens?: (lensType: string) => void;
  lensType?: string;
  shouldShowLensFocus?: boolean;
}

const ResultSection: React.FC<ResultSectionProps> = ({ 
  diceValue, 
  promptStage, 
  rollAgain,
  advancePrompt,
  reRollPrompt,
  twistActive,
  activateTwist,
  isSpinning = false,
  rerollCount = 0,
  onFeedback,
  onSelectLens,
  lensType,
  shouldShowLensFocus = false
}) => {
  const { autoProceed, darkMode, enableFeedback, enableLensFocus } = useSettings();
  const [showingFeedback, setShowingFeedback] = useState(false);
  const [selectedFinal, setSelectedFinal] = useState("");

  // Random final prompt options
  const finalPrompts = [
    "Is there something small you feel able to try now?",
    "Did this help you see what might be possible again?",
    "Want to try one thing from where you are now?",
    "What's one tiny step you could take?",
    "How might you move with this insight?",
    "What feels possible from here?"
  ];

  // Select a random final prompt when we reach the nextStep stage
  React.useEffect(() => {
    if (promptStage === "nextStep" && !selectedFinal) {
      const randomIndex = Math.floor(Math.random() * finalPrompts.length);
      setSelectedFinal(finalPrompts[randomIndex]);
    }
  }, [promptStage, selectedFinal, finalPrompts]);

  // Handle feedback submission
  const handleFeedback = (feedbackType: "positive" | "neutral" | "negative") => {
    if (onFeedback) {
      onFeedback(feedbackType);
    }
    
    setShowingFeedback(false);
  };

  // Handle lens selection
  const handleLensSelect = (lensType: string) => {
    console.log("Lens selected in ResultSection:", lensType);
    if (onSelectLens) {
      onSelectLens(lensType);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      <PromptDisplay 
        diceValue={diceValue}
        promptStage={promptStage}
        className="w-full mb-8"
        twistActive={twistActive}
        lensType={lensType}
      />
      
      {!autoProceed && promptStage === "perception" && reRollPrompt && (
        <div className="flex justify-center mb-4 flex-wrap gap-2">
          <Button
            onClick={reRollPrompt}
            variant="outline"
            size="sm"
            disabled={isSpinning}
            className={cn(
              "border flex items-center gap-2",
              darkMode 
                ? "border-[#00FF99] text-[#00FF99] hover:bg-[#00FF99]/10" 
                : "border-[#6127A0] text-[#6127A0] hover:bg-[#6127A0]/10"
            )}
          >
            <RefreshCw className={cn("h-4 w-4", isSpinning && "animate-spin")} /> Re-roll
          </Button>
          
          {advancePrompt && (
            <Button
              onClick={advancePrompt}
              variant="outline"
              size="sm"
              disabled={isSpinning}
              className={cn(
                "border flex items-center gap-2",
                darkMode 
                  ? "border-[#00FF99] text-[#00FF99] hover:bg-[#00FF99]/10"
                  : "border-[#6127A0] text-[#6127A0] hover:bg-[#6127A0]/10"
              )}
            >
              Continue <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
      
      {!autoProceed && promptStage === "intention" && advancePrompt && (
        <div className="flex justify-center mb-4">
          <Button
            onClick={advancePrompt}
            variant="outline"
            size="sm"
            disabled={isSpinning}
            className={cn(
              "border flex items-center gap-2",
              darkMode 
                ? "border-[#00FF99] text-[#00FF99] hover:bg-[#00FF99]/10"
                : "border-[#6127A0] text-[#6127A0] hover:bg-[#6127A0]/10"
            )}
          >
            Continue <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      {promptStage === "nextStep" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="w-full"
        >
          <div className={cn(
            "border-l-4 border-[#2F6665] p-4 rounded-lg shadow-sm w-full mb-6 text-left",
            darkMode ? "bg-[#2d1a45]/90 text-[#FBF3F1]" : "bg-white"
          )}>
            <h3 className="text-[#2F6665] font-medium text-sm uppercase tracking-wider mb-1">
              {selectedFinal || "What's your next right step?"}
            </h3>
            <p className={cn(
              "italic",
              darkMode ? "text-[#FBF3F1]" : "text-gray-700"
            )}>Take a moment to consider what action comes next for you.</p>
          </div>
          
          {enableFeedback && !showingFeedback && (
            <div className="mb-6">
              <FeedbackEmojis onFeedback={handleFeedback} />
            </div>
          )}
          
          <div className="flex justify-center flex-wrap gap-3 mt-4">
            {/* Only show the Add a Twist button if twist is not active and activateTwist is provided */}
            {!twistActive && activateTwist && (
              <Button 
                onClick={activateTwist}
                variant="outline"
                size="lg"
                disabled={isSpinning}
                className={cn(
                  "border px-6 py-2 rounded-full flex items-center gap-2",
                  darkMode 
                    ? "border-[#00FF99] text-[#00FF99] hover:bg-[#00FF99]/10"
                    : "border-[#6127A0] text-[#6127A0] hover:bg-[#6127A0]/10"
                )}
              >
                <Dices className="w-4 h-4 mr-1" />
                🎲 Add a Twist
              </Button>
            )}
            
            <Button 
              onClick={rollAgain} 
              variant="outline"
              size="lg"
              disabled={isSpinning}
              className={cn(
                "border px-6 py-2 rounded-full flex items-center gap-2",
                darkMode 
                  ? "border-[#00FF99] text-[#00FF99] hover:bg-[#00FF99]/10"
                  : "border-[#6127A0] text-[#6127A0] hover:bg-[#6127A0]/10"
              )}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Roll Again
            </Button>
          </div>
        </motion.div>
      )}
      
      {promptStage === "twist" && (
        <div className="flex justify-center">
          <Button 
            onClick={rollAgain} 
            variant="outline"
            size="lg"
            disabled={isSpinning}
            className={cn(
              "border px-6 py-2 rounded-full flex items-center gap-2",
              darkMode 
                ? "border-[#00FF99] text-[#00FF99] hover:bg-[#00FF99]/10"
                : "border-[#6127A0] text-[#6127A0] hover:bg-[#6127A0]/10"
            )}
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Roll Again
          </Button>
        </div>
      )}
      
      {promptStage === "lensFocus" && (
        <div className="flex justify-center">
          <Button 
            onClick={rollAgain} 
            variant="outline"
            size="lg"
            disabled={isSpinning}
            className={cn(
              "border px-6 py-2 rounded-full flex items-center gap-2",
              darkMode 
                ? "border-[#00FF99] text-[#00FF99] hover:bg-[#00FF99]/10"
                : "border-[#6127A0] text-[#6127A0] hover:bg-[#6127A0]/10"
            )}
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Roll Again
          </Button>
        </div>
      )}
      
      {/* Conditionally show the dialog only if Untangle Mode is enabled in settings */}
      {enableLensFocus && shouldShowLensFocus && (
        <LensFocusDialog 
          open={shouldShowLensFocus}
          onOpenChange={(open) => {
            console.log("Dialog open change:", open);
            // If manually closed without selection, we need to handle that
            if (!open && onSelectLens && promptStage !== "lensFocus") {
              // This is needed to prevent the dialog from closing immediately
              const timer = setTimeout(() => {
                console.log("Auto-closing dialog");
              }, 300);
              return () => clearTimeout(timer);
            }
          }}
          onSelectLens={handleLensSelect}
        />
      )}
    </motion.div>
  );
};

export default ResultSection;

// Helper to conditionally join classNames
function cn(...classes: (string | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}
